import React, { useState, useEffect } from 'react';
import { RankingRepository } from '../services/repository.js';
import TournamentTypeFlag from './TournamentTypeFlag.jsx';
import { DateParser } from '../utils/DateParser.js';
import './RankingDetails.css';

const RankingDetails = () => {
    const [ranking, setRanking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTab, setSelectedTab] = useState('open'); // 'open', 'women', 'mexicano'
    const [selectedDate, setSelectedDate] = useState(Math.floor(Date.now() / 1000));

    useEffect(() => {
        loadRanking();
    }, [selectedTab, selectedDate]);

    const getTournamentTypeFromTab = (tab) => {
        switch (tab) {
            case 'open': return 'open';
            case 'women': return 'women';
            case 'mexicano': return 'mexicano';
            default: return 'open';
        }
    };

    const loadRanking = async () => {
        try {
            setLoading(true);
            setError(null);
            const tournamentType = getTournamentTypeFromTab(selectedTab);
            const data = await RankingRepository.getRanking({
                type: tournamentType,
                date: selectedDate
            });
            setRanking(data);
        } catch (err) {
            console.error('Error loading ranking:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (seconds) => {
        if (!seconds) return 'Nieznana data';
        try {
            // Date from endpoint is in seconds since 1970 format
            return DateParser.formatSeconds(seconds, 'datetime');
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Nieznana data';
        }
    };

    const getRankingPeriodText = (periodType) => {
        switch (periodType) {
            case 'month':
                return 'Miesięczny';
            case 'quarter':
                return 'Kwartalny';
            case 'halfYear':
                return 'Półroczny';
            case 'year':
                return 'Roczny';
            default:
                return periodType;
        }
    };

    const getMedalEmoji = (rank) => {
        switch (rank) {
            case 1:
                return '🥇';
            case 2:
                return '🥈';
            case 3:
                return '🥉';
            default:
                return '';
        }
    };

    const getRankColor = (rank) => {
        switch (rank) {
            case 1:
                return 'rank-gold';
            case 2:
                return 'rank-silver';
            case 3:
                return 'rank-bronze';
            default:
                return 'rank-default';
        }
    };

    if (loading) {
        return (
            <div className="ranking-details-container">
                <div className="loading-message">Ładowanie rankingu...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="ranking-details-container">
                <div className="error-message">
                    Błąd ładowania rankingu: {error}
                    <button onClick={loadRanking} className="retry-button">
                        Spróbuj ponownie
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="ranking-page-container">
            {ranking && (
                <>
                    <div className="ranking-page-tabs">
                        <button 
                            className={`ranking-tab ${selectedTab === 'open' ? 'active' : ''}`}
                            onClick={() => setSelectedTab('open')}
                        >
                            Open
                        </button>
                        <button 
                            className={`ranking-tab ${selectedTab === 'women' ? 'active' : ''}`}
                            onClick={() => setSelectedTab('women')}
                        >
                            Kobiety
                        </button>
                        <button 
                            className={`ranking-tab ${selectedTab === 'mexicano' ? 'active' : ''}`}
                            onClick={() => setSelectedTab('mexicano')}
                        >
                            Mexicano
                        </button>
                    </div>
                    
                    <div className="ranking-page-calculated-only">
                        <span>Obliczono: {formatDateTime(ranking.calculatedAt)}</span>
                    </div>

                    {ranking.playerRankings && ranking.playerRankings.length > 0 ? (
                        <div className="ranking-page-list">
                            {ranking.playerRankings
                                .sort((a, b) => a.rank - b.rank)
                                .map((playerRanking, index) => (
                                    <div 
                                        key={playerRanking.id} 
                                        className={`ranking-page-row ${getRankColor(playerRanking.rank)}`}
                                    >
                                        <div className="ranking-page-rank-cell">
                                            <span className="ranking-page-rank-number">
                                                {getMedalEmoji(playerRanking.rank)}
                                                {playerRanking.rank}
                                            </span>
                                        </div>
                                        <div className="ranking-page-player-cell">
                                            <span className="ranking-page-player-name">
                                                {playerRanking.participant.name}
                                            </span>
                                        </div>
                                        <div className="ranking-page-points-cell">
                                            <span className="ranking-page-points-value">
                                                {playerRanking.totalPoints}
                                            </span>
                                            <span className="ranking-page-points-label">pkt</span>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <div className="ranking-page-no-data">
                            <div className="ranking-page-no-data-icon">📊</div>
                            <h3>Brak danych rankingu</h3>
                            <p>Nie znaleziono danych rankingu dla wybranych kryteriów.</p>
                            <p>Spróbuj wybrać inny typ turnieju lub datę.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default RankingDetails;
